import { jwtDecode } from 'jwt-decode';
import React from 'react';
import {
    FormEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocation, useNavigate } from 'react-router-dom';

import i18n from '@nextroll/ar-i18n';
// @ts-ignore - no types for telemetry
import telemetry from '@nextroll/telemetry';

import {
    initFormState,
    setFieldFactory,
    setGoogleIdTokenFactory,
    setReCaptchaFactory,
    submitFactory,
} from '../../actions/sign-up/SignUpActions';
import FormSpinner from '../../components/FormSpinner';
import GoogleButton, {
    CredentialResponse,
    IIdInfo,
} from '../../components/GoogleButton';
import InlineFeedback from '../../components/InlineFeedback';
import { ApiError } from '../../components/errors';
import Title from '../../components/sign-up/Title';
import ToS from '../../components/sign-up/ToS';
import { API_STATUSES, SignUpModes } from '../../constants';
import ThemeWrapperContext from '../../contexts/ThemeWrapperContext';
import SignUpContext, {
    INITIAL_SIGN_UP_FORM_STATE,
} from '../../contexts/sign-up/SignUpContext';
import useMode from '../../hooks/sign-up/useMode';
import SignUpReducer from '../../reducers/sign-up/SignUpReducer';
import { areFieldsValid } from '../../utils/sign-up/fields';
import {
    loadMarketingIntegrations,
    removeMarketingIntegrations,
} from '../../utils/sign-up/marketing';
import CompanyMarket from './fields/CompanyMarket';
import CompanyPhone from './fields/CompanyPhone';
import CountryCode from './fields/CountryCode';
import Email, { validate as validateEmail } from './fields/Email';
import FirstName, { validate as validateFirstName } from './fields/FirstName';
import LastName, { validate as validateLastName } from './fields/LastName';
import Password from './fields/Password';
import Region from './fields/Region';
import Url, { validate as validateUrl } from './fields/Url';

const GIS_TELEMETRY_EVENT = 'entryhall-gis-interaction';

const _SignUpWithGoogleMessage = () => (
    <div id='eh-google-signup-g-info' className='ar-alerts-container'>
        <div
            role='alert'
            className='ar-alert-inline ar-alert ar-alert-info'
            style={{ margin: 0 }}
        >
            <div className='ar-alert-content-container'>
                <i className='ar-alert-icon fa-info-circle' aria-hidden />
                <div className='ar-alert-text'>
                    <p className='ar-alert-title'>
                        {i18n.gettext('Signing up with Google')}
                    </p>
                    <span className='ar-alert-message'>
                        {i18n.gettext(
                            "We've filled out your account information from your Google sign-in. We need a few more details to complete your account."
                        )}
                    </span>
                </div>
            </div>
        </div>
    </div>
);

interface ISignUpFormProps {
    mode: SignUpModes;
    source?: string;
}
export const _SignUpForm = ({ mode, source }: ISignUpFormProps) => {
    const { state, dispatch } = useContext(SignUpContext);
    const { businessUnit, supportEmail } = useContext(ThemeWrapperContext);
    const [redirectSpinner, setRedirectSpinner] = useState(false);

    useMode(mode);

    const navigate = useNavigate();
    const navigateToSignIn = () => navigate('../signin');
    const { search } = useLocation();

    const fields = state.get('fields');
    const extraFields = state.get('extraFields');
    extraFields.source = source || extraFields.source;
    const reCaptcha = state.get('reCaptcha');
    const googleIdToken = state.get('googleIdToken');
    const submitState = state.get('submit');

    const isGSignUp = googleIdToken !== '';

    const disabled = areFieldsValid(fields) === false;

    // form width state for rendering responsive Google button
    const [formWidth, setFormWidth] = useState<number>(400); // default width
    const formResizeObserverRef = useRef<ResizeObserver | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(search);
        initFormState(dispatch, params);

        if (params.get('source') === 'shopify_2022_03') {
            setRedirectSpinner(true);
            window.location.href = `/welcome/shopify-signup${search}`;
        }

        // set up resize observer to update form width state
        const form = document.querySelector('#registerForm');
        if (form) {
            formResizeObserverRef.current = new ResizeObserver(([form]) => {
                // Google button requires integer width value to avoid strange artifacts
                const updatedWidth = Math.floor(form.contentRect?.width || 400);
                setFormWidth(updatedWidth);
            });
            formResizeObserverRef.current.observe(form);
        }

        return () => {
            if (form && formResizeObserverRef.current) {
                formResizeObserverRef.current.unobserve(form);
                formResizeObserverRef.current.disconnect();
            }
        };
    }, []);

    useEffect(() => {
        if (submitState.status === API_STATUSES.SUCCESS) {
            if (submitState.redirect === 'internal_redirect') {
                navigate(`../${submitState.to}`);
            } else if (submitState.redirect === 'external_redirect') {
                setRedirectSpinner(true);
                window.location.href = submitState.to;
            }
        }
    }, [submitState]);

    const setReCaptcha = setReCaptchaFactory(
        dispatch,
        useGoogleReCaptcha().executeRecaptcha
    );
    const setGoogleToken = setGoogleIdTokenFactory(dispatch);
    const setEmail = setFieldFactory(dispatch, 'email');
    const setFirstName = setFieldFactory(dispatch, 'firstName');
    const setLastName = setFieldFactory(dispatch, 'lastName');
    const setUrl = setFieldFactory(dispatch, 'url');
    const setPassword = setFieldFactory(dispatch, 'password');

    const submit = submitFactory(
        dispatch,
        fields,
        businessUnit,
        googleIdToken,
        extraFields,
        {
            ...reCaptcha,
            executeReCaptcha: setReCaptcha,
        }
    );

    /**
     * use event callbacks
     */

    const onGoogleSuccess = (response: CredentialResponse) => {
        setGoogleToken(response.credential);
        const idInfo: IIdInfo = jwtDecode(response.credential);
        const emailErr = validateEmail(idInfo.email);
        setEmail(idInfo.email, emailErr === null, emailErr);
        const firstNameErr = validateFirstName(idInfo.given_name);
        setFirstName(idInfo.given_name, firstNameErr === null, firstNameErr);
        const lastNameErr = validateLastName(idInfo.family_name);
        setLastName(idInfo.family_name, lastNameErr === null, lastNameErr);
        const url = idInfo.hd || '';
        const urlErr = validateUrl(url);
        setUrl(url, urlErr === null, urlErr);

        setPassword('', true, null); // Set 'valid' empty password
        telemetry.track(GIS_TELEMETRY_EVENT, {
            step: 'completion',
            context: 'signup',
        });
    };

    const onFormSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            !disabled &&
                submitState.status !== API_STATUSES.IN_PROGRESS &&
                submit?.();
            return false;
        },
        [disabled, submitState.status, submit]
    );

    //
    // use a specified parameter to determine whether to render single-column layout
    // TODO: remove after the final layout decision is made
    //
    const isSingleColumnLayout = useMemo(
        () => new URLSearchParams(search)?.has('singleColumn'),
        [search]
    );

    /**
     * component rendering logic
     */

    if (redirectSpinner) {
        return <FormSpinner />;
    }

    const signUpWithGoogle = isGSignUp ? (
        <_SignUpWithGoogleMessage />
    ) : (
        <GoogleButton
            onSuccess={onGoogleSuccess}
            onError={() => {
                // @FIXME implement error handling
                console.log('Error from GoogleButton');
                telemetry.track(GIS_TELEMETRY_EVENT, {
                    step: 'error',
                    context: 'signup',
                });
            }}
            click_listener={() => {
                telemetry.track(GIS_TELEMETRY_EVENT, {
                    step: 'start',
                    context: 'signup',
                });
            }}
            text='signup_with'
            width={formWidth || 400}
        />
    );

    const components = [
        // header components
        <div className='eh-or-separator'>
            <span>{i18n.gettext('Or')}</span>
        </div>,
        submitState.showError && (
            <div className='eh-card-header-error'>
                <ApiError
                    error={submitState.error}
                    supportEmailAddress={supportEmail}
                />
            </div>
        ),

        // body components
        <FirstName />,
        <LastName />,
        <Email />,
        <CompanyPhone />,
        <Url />,
        <CompanyMarket />,
        <CountryCode />,
        <Region />,
        <Password />,

        // footer components
        <ToS />,
        <button
            type='submit'
            id='ar-signup-button'
            className='btn btn-primary btn-block'
            disabled={disabled}
        >
            {i18n.gettext('SIGN UP')}
            <InlineFeedback status={submitState.status} />
        </button>,
        <div className='extra-info'>
            {i18n.gettext('Already have an account?')}
            &nbsp;&nbsp;
            <a
                role='button'
                onClick={navigateToSignIn}
                aria-label='redirect to sign in page'
            >
                {i18n.gettext('Sign In')}
            </a>
        </div>,
    ]
        .filter(Boolean)
        .map((component, i) =>
            React.cloneElement(component, { key: `signup-form-field-${i}` })
        );

    return (
        <div className='eh-card-block eh-form-sign-up'>
            <form
                id='registerForm'
                className={`eh-card-body${
                    isSingleColumnLayout ? ' single-column' : ''
                }`}
                onSubmit={onFormSubmit}
            >
                <Title isGoogleSignUp={isGSignUp} />
                {signUpWithGoogle}
                {components}
            </form>
        </div>
    );
};

// SignUpForm is a wrapper around _SignUpForm that provides the context.
// This works differently from other forms, to allow using this form in various entry-hall pages
// without having to pass to configure the context in each page.
const SignUpForm = (props: ISignUpFormProps) => {
    const [state, dispatch] = useReducer(
        SignUpReducer,
        INITIAL_SIGN_UP_FORM_STATE
    );

    useEffect(() => {
        loadMarketingIntegrations();
        return () => {
            removeMarketingIntegrations();
        };
    }, []);

    return (
        <SignUpContext.Provider value={{ state, dispatch }}>
            <_SignUpForm {...props} />
        </SignUpContext.Provider>
    );
};

export default SignUpForm;
